import type { FC, ReactNode } from 'react'
import type { ConsentChoice } from '@/lib/consent'

import { useCallback, useEffect, useState } from 'react'
import Link from 'next/link'
import { Cookie } from 'lucide-react'

import { readConsent, subscribeToSettingsOpen, writeConsent } from '@/lib/consent'

export const CookieBanner: FC = (): ReactNode => {
    const [visible, setVisible] = useState(false)

    useEffect(() => {
        // Decided on the client only, so the server-rendered markup stays identical
        // for every visitor and nothing flashes for people who already chose.
        if (!readConsent()) setVisible(true)

        return subscribeToSettingsOpen(() => setVisible(true))
    }, [])

    const decide = useCallback((choice: ConsentChoice): void => {
        writeConsent(choice)
        setVisible(false)
    }, [])

    if (!visible) return null

    return (
        <div
            role='region'
            aria-label='Cookie preferences'
            className='fixed inset-x-0 bottom-0 z-50 p-4 duration-500 animate-in fade-in slide-in-from-bottom-6 sm:p-6'
        >
            <div className='mx-auto flex max-w-4xl flex-col gap-5 rounded-xl border border-border bg-card p-5 shadow-lg sm:p-6 lg:flex-row lg:items-center lg:gap-8'>
                <div className='flex flex-1 gap-4'>
                    <span className='hidden size-11 shrink-0 place-items-center rounded-lg bg-primary/10 text-primary sm:grid'>
                        <Cookie className='size-5' />
                    </span>

                    <div>
                        <h2 className='font-heading text-base font-semibold tracking-tight text-foreground'>
                            We use cookies
                        </h2>

                        <p className='mt-1.5 text-sm leading-relaxed text-muted-foreground'>
                            We measure how visitors use our guides so we can improve them. Analytics
                            cookies load only if you accept, and you can change your mind at any time.
                            Read our{' '}
                            <Link
                                href='/privacy'
                                className='font-medium text-primary underline underline-offset-2 hover:no-underline'
                            >
                                Privacy &amp; Cookie Policy
                            </Link>
                            .
                        </p>
                    </div>
                </div>

                <div className='flex shrink-0 flex-col gap-3 sm:flex-row lg:justify-end'>
                    <button
                        type='button'
                        onClick={() => decide('denied')}
                        className='inline-flex h-11 items-center justify-center rounded-md border border-border px-5 text-sm font-semibold text-foreground transition-colors hover:bg-secondary'
                    >
                        Decline
                    </button>

                    <button
                        type='button'
                        onClick={() => decide('granted')}
                        className='inline-flex h-11 items-center justify-center rounded-md bg-primary px-5 text-sm font-semibold text-primary-foreground transition-colors hover:bg-primary/90'
                    >
                        Accept cookies
                    </button>
                </div>
            </div>
        </div>
    )
}
